import { accountsApiRtkQuery } from '@chillibean/accounts-api-client-sdk';
import { useToast } from '@chillibean/component-library';
import { trialContainersApiRtkQuery } from '@chillibean/trial-containers-api-client-sdk';
import { memo, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { AssetRenderer } from '../../../assets/AssetRenderer';
import { useAccountData, useGetAccountQuery } from '../../redux/accountSlice';
import { selectInternalResult } from '../../redux/internal.slice';
import { setUserActivity } from '../../redux/userActivity.slice';
import ActivityLogger from './ActivityLogger';

const AssetRendererMemoized = memo(AssetRenderer);

/**
 * Requirements:
 * - Asset preview should take up as much space as possible to fill whole of the screen.
 */
export function LibraryAssetContainer() {
  // VARIABLES
  const ASSET_RENDERER_HEIGHT_OFFSET = 410;

  // HOOKS
  // React
  const wrapperRef = useRef();
  // Store
  const dispatch = useDispatch();
  const { accountId, assetId, authenticityToken, libraryId, user } = useSelector(selectInternalResult);
  const { account } = useAccountData({ accountId });
  // RTK Query
  const { useGetChapterDefinitionsQuery } = accountsApiRtkQuery;
  const { useGetAssetQuery, useGetAssetChaptersQuery, useUpdateAssetChaptersMutation } = trialContainersApiRtkQuery;
  useGetAccountQuery({ id: accountId }, { skip: !accountId });
  const { data: chapterDefinitions } = useGetChapterDefinitionsQuery({ accountId }, { skip: !accountId });
  const {
    data: asset,
    error: assetError,
    isLoading: isAssetLoading,
  } = useGetAssetQuery(
    {
      accountId: accountId,
      assetId: assetId,
      trialContainerId: libraryId,
    },
    { skip: !accountId || !assetId || !libraryId },
  );
  const { data: chapters, isLoading: isChaptersLoading } = useGetAssetChaptersQuery(
    { accountId: accountId, assetId: assetId, trialContainerId: libraryId },
    { skip: !accountId || !assetId || !libraryId },
  );
  const [updateAssetChapters] = useUpdateAssetChaptersMutation();
  // Custom
  const { showErrorToast, showSuccessToast } = useToast();

  const onChaptersSubmit = useCallback(
    async (chaptersData) => {
      try {
        const response = await updateAssetChapters({
          accountId: accountId,
          assetId: assetId,
          trialContainerId: libraryId,
          body: { authenticityToken: authenticityToken, chapters: chaptersData },
        }).unwrap();

        showSuccessToast('Chapters Updated');

        return Promise.resolve(response);
      } catch (error) {
        if (error.data && error.data.errors.message) {
          showErrorToast(error.data.errors);
        }

        return Promise.reject(error.data.errors.fields);
      }
    },
    [accountId, assetId, libraryId, authenticityToken],
  );

  const onVideoPlay = useCallback(() => {
    dispatch(setUserActivity('watching video'));
  }, []);

  const onResize = useCallback(() => {
    if (!wrapperRef.current) return;

    const height = window.innerHeight - ASSET_RENDERER_HEIGHT_OFFSET;
    wrapperRef.current.style.setProperty('--height', `${height}px`);
  }, [wrapperRef]);

  useEffect(() => {
    onResize();
  }, [isAssetLoading]);

  useLayoutEffect(() => {
    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <>
      <ActivityLogger />
      <StyledWrapper ref={wrapperRef}>
        <AssetRendererMemoized
          account={account}
          chapterDefinitions={chapterDefinitions}
          chapters={chapters}
          data={asset}
          error={assetError}
          loading={isAssetLoading || isChaptersLoading}
          onVideoPlay={onVideoPlay}
          onChaptersSubmit={onChaptersSubmit}
          user={user}
        />
      </StyledWrapper>
    </>
  );
}

const StyledWrapper = styled.div`
  --height: 400px;

  display: flex;

  height: var(--height);
  max-width: 70%;
  margin: auto;
`;

export default LibraryAssetContainer;
